import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { SocialChannelState } from "./app.models";
import { SocialChannel } from "@/database/user/model";
import { UserModel, ProductModel } from "@/database";

interface Option {
  label: string;
  value: string;
}

interface LayoutState {
  activeNavLink: string;
  activeSideBarLink: string;
  isSideNavClose: boolean;
  isModalOpen: boolean;
  isUploadProgressOpen: boolean;
  businessConstants: [];
  softRefresh: boolean;
  isMediaDetailForm: boolean;

  isSideNavHidden: boolean;
  globalSearchResultList: [];
  categories: Record<string, Option[]>;
  [key: string]: any;
}

interface SetStateValuePayload {
  key: string;
  value: any;
  addToPreviousValue?: boolean;
  json?: boolean;
}

interface ToggleStateValuePayload {
  key: string;
}

const initialState: LayoutState = {
  activeNavLink: "vod",
  activeSideBarLink: "home",
  isSideNavClose: true,
  isModalOpen: false,
  isUploadProgressOpen: false,
  businessConstants: [],
  orgBusTeamConstants: [],
  businessProductConstants: [],
  categories: {},
  languages: [] as Option[],
  metaLanguage: [] as Option[],
  category: [] as any,
  language: [] as any,
  tag: [] as any,
  cast: [] as any,
  genre: [] as any,
  productList: [] as ProductModel[],
  currentSocial: [],
  constUserList: [] as UserModel[],
  businesses: [],
  agencies: [] as Option[],
  softRefresh: false,
  isMediaDetailForm: false,
  isSideNavHidden: false,
  globalSearchResultList: [],
  rootLoading: true,
  userSocialChannels: {
    isLoading: true,
    socialChannelsList: [] as SocialChannel[],
    error: "",
  },
  notifications: {
    isLoading: false,
    list: [],
    error: "",
    currentNotification: {},
  },
  agencyList: [],
  youtubePlaylist: [],
};

export const App = createSlice({
  name: "app",
  initialState,
  reducers: {
    setSoftRefresh: (state, action) => {
      state.softRefresh = action.payload;
    },
    setIsMediaDetailForm: (state, action) => {
      state.isMediaDetailForm = action.payload;
    },
    toggleSideNav: (state, action) => {
      state.isSideNavHidden = action.payload;
    },
    setStateValue: (state, action: PayloadAction<SetStateValuePayload>) => {
      const { key, value, addToPreviousValue, json } = action.payload;

      if (
        addToPreviousValue === true &&
        (json === false || json === undefined)
      ) {
        state[key] += value;
        return;
      } else if (addToPreviousValue === true && json === true) {
        state[key].push(JSON.parse(value));
        return;
      }

      state[key] = value;
    },
    setYoutubePlaylist: (state, action) => {
      state.youtubePlaylist = action.payload;
    },
    toggleStateValue: (
      state,
      action: PayloadAction<ToggleStateValuePayload>
    ) => {
      const { key } = action.payload;
      state[key] = !state[key];
    },
    updateModal: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    showUploadProgress: (state) => {
      state.isUploadProgressOpen = true;
    },
    hideUploadProgress: (state) => {
      state.isUploadProgressOpen = false;
    },
    toggleIsLoading: (state, action: PayloadAction<{ key: string }>) => {
      state[action.payload.key] = !state[action.payload.key];
    },
    setBusinessConstants: (state, action) => {
      state.businessConstants = action.payload.businessList;
      state.isBusinessConstantLoding = false;
    },
    setBusinesses: (state, action) => {
      state.business = action.payload;
    },
    setNotifications: (state, action) => {
      state.notifications = action.payload;
    },
    setCurrentNotification: (state, action) => {
      state.notifications.currentNotification = action.payload;
    },
    setOrganizationConstants: (state, action) => {
      state.organizationConstants = action.payload;
      state.isOrganiationConstantLoading = false;
    },
    setTeamConstants: (state, action) => {
      state.teamConstants = action.payload;
      state.isTeamConstantLoading = false;
    },
    setOrgBusTeamConstants: (state, action) => {
      state.orgBusTeamConstants = action.payload;
    },
    setBusinessProductConstants: (state, action) => {
      state.businessProductConstants = action.payload;
    },
    setCategories: (state, action) => {
      state.categories = action.payload;
    },
    setMetaLanguage: (state, action) => {
      state.metaLanguage = action.payload;
    },
    setCategory: (state, action) => {
      state.category = action.payload;
    },
    setLanguage: (state, action) => {
      state.language = action.payload;
    },
    setTag: (state, action) => {
      state.tag = action.payload;
    },
    setCast: (state, action) => {
      state.cast = action.payload;
    },
    setGenre: (state, action) => {
      state.genre = action.payload;
    },
    setConstUserList: (state, action) => {
      state.constUserList = action.payload;
    },
    setProductList: (state, action) => {
      state.productList = action.payload;
    },
    setAgencies: (state, action) => {
      state.agencies = action.payload;
    },
    setIsRootLoading: (state, action) => {
      state.rootLoading = action.payload;
    },
    setGlobalSearchList: (state, action) => {
      state.globalSearchResultList = action.payload.list;
    },

    setAgencyList: (state, action) => {
      state.agencyList = action.payload;
    },

    setUserSocialChannels: (
      state,
      { payload }: PayloadAction<SocialChannelState>
    ) => {
      state.userSocialChannels = {
        ...state.userSocialChannels,
        isLoading:
          payload?.isLoading ??
          state.userSocialChannels.isLoadingSocialChannels,
        socialChannelsList:
          payload?.data?.list ?? state.userSocialChannels.socialChannelsList,
        error: payload?.error ?? state.userSocialChannels.error,
      };
    },
    setCurrentSocial: (state, { payload }: PayloadAction<any>) => {
      const { socialChannelsList } = state.userSocialChannels;
      const index = socialChannelsList.findIndex(
        (channel: any) => channel.id === payload.id
      );
      if (index !== -1) {
        const updatedChannelsList = socialChannelsList.map((channel: any) =>
          channel.id === payload.id ? { ...channel, ...payload } : channel
        );
        state.userSocialChannels.socialChannelsList = updatedChannelsList;
      }
    },
  },
});

// creating actions from reducers
export const {
  setIsRootLoading,
  setStateValue,
  toggleStateValue,
  updateModal,
  showUploadProgress,
  hideUploadProgress,
  toggleIsLoading,
  setBusinessConstants,
  setOrganizationConstants,
  setTeamConstants,
  setOrgBusTeamConstants,
  setBusinessProductConstants,
  setCategories,
  setMetaLanguage,
  setProductList,
  setAgencies,
  setSoftRefresh,
  setIsMediaDetailForm,
  setBusinesses,
  toggleSideNav,
  setGlobalSearchList,
  setNotifications,
  setYoutubePlaylist,
  setCurrentSocial,
  setCurrentNotification,
  setUserSocialChannels,
  setCategory,
  setLanguage,
  setTag,
  setCast,
  setGenre,
  setConstUserList,
  setAgencyList,
} = App.actions;

export default App.reducer;
